import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Circle,
  Calendar as CalendarIcon,
  Repeat,
  Flag,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useListTasks,
  useToggleCompleteTask,
  getListTasksQueryKey,
  type Task,
} from "@workspace/api-client-react";
import { format, isPast, isToday } from "date-fns";
import { cn } from "@/lib/utils";
import { TaskEditSheet } from "@/components/task-edit-sheet";

const PRIORITY_STYLES: Record<string, string> = {
  none: "text-muted-foreground",
  low: "text-blue-500",
  medium: "text-orange-500",
  high: "text-red-500",
};

export function TasksPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState<"active" | "completed" | "all">(
    "active",
  );
  const [editing, setEditing] = React.useState<Task | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editNonce, setEditNonce] = React.useState(0);
  const { data: tasks = [], isLoading } = useListTasks();
  const toggleComplete = useToggleCompleteTask();

  const openEdit = (task: Task) => {
    setEditing(task);
    setEditNonce((n) => n + 1);
    setEditOpen(true);
  };

  const filteredTasks = tasks.filter((t) =>
    filter === "all" ? true : filter === "active" ? !t.completed : t.completed,
  );

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (!a.dueAt && !b.dueAt) return 0;
    if (!a.dueAt) return 1;
    if (!b.dueAt) return -1;
    return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
  });

  const getRepeatText = (task: Task): string => {
    switch (task.repeat) {
      case "daily":
        return "Every day";
      case "weekly":
        return "Every week";
      case "weekday":
        return "Every weekday";
      case "monthly":
        return "Every month";
      case "yearly":
        return "Every year";
      case "custom": {
        const n = task.repeatInterval ?? 1;
        const unit = task.repeatUnit ?? "day";
        let text = n === 1 ? "Every " + unit : "Every " + n + " " + unit + "s";
        if (task.repeatEndsAt) {
          text +=
            ", until " +
            new Date(task.repeatEndsAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
        }
        return text;
      }
      default:
        return task.repeat;
    }
  };

  const handleToggle = (task: Task) => {
    toggleComplete.mutate(
      { id: task.id },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData<Task[]>(getListTasksQueryKey(), (list) =>
            list?.map((t) => (t.id === updated.id ? updated : t)),
          );
          if (updated.completed && updated.repeat !== "none") {
            queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() });
          }
        },
      },
    );
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem-3.25rem)] flex flex-col px-4 relative">
      <div className="flex items-center gap-2 pt-3 pb-3 shrink-0 overflow-x-auto">
        {(["active", "completed", "all"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              "shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all capitalize",
              filter === f
                ? "gradient-primary text-white border-transparent"
                : "bg-card/50 border-border/50 text-muted-foreground",
            )}
          >
            {f}
          </button>
        ))}
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-10">
              Loading tasks...
            </div>
          ) : sortedTasks.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              Nothing here. Add a task with "Remember this" on the home screen.
            </div>
          ) : (
            sortedTasks.map((task) => {
              const overdue =
                !!task.dueAt &&
                !task.completed &&
                isPast(new Date(task.dueAt)) &&
                !isToday(new Date(task.dueAt));
              return (
                <div
                  key={task.id}
                  onClick={() => openEdit(task)}
                  className="group relative bg-card border rounded-2xl px-4 py-3.5 shadow-sm flex items-start gap-3 cursor-pointer active:bg-muted/40 transition-colors"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(task);
                    }}
                    className="mt-0.5 shrink-0 p-1.5 -m-1.5"
                    aria-label="Toggle complete"
                  >
                    {task.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-[15px] font-medium break-words",
                        task.completed && "line-through text-muted-foreground",
                      )}
                    >
                      {task.title}
                    </p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {task.dueAt && (
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium",
                            overdue ? "text-red-500" : "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="h-3 w-3" />
                          {format(new Date(task.dueAt), "MMM d, h:mm a")}
                        </span>
                      )}
                      {task.priority !== "none" && (
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium capitalize",
                            PRIORITY_STYLES[task.priority],
                          )}
                        >
                          <Flag className="h-3 w-3" />
                          {task.priority}
                        </span>
                      )}
                      {task.repeat !== "none" && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                          <Repeat className="h-3 w-3" />
                          {getRepeatText(task)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {editing && (
        <TaskEditSheet
          key={`${editing.id}-${editNonce}`}
          task={editing}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}
